import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listRmas, listRmaLines, listRmaSkus } from "@/lib/returns.functions";
import { bulkReceiveExpected } from "@/lib/returns-receiving.functions";
import { lookupLocationByCode } from "@/lib/putaway-directives.functions";
import { RfShell, RfEmpty } from "@/components/rf/rf-shell";
import { ScanInput } from "@/components/ui/scan-input";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useScope } from "@/hooks/use-scope";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export const Route = createFileRoute("/_authenticated/app/rf/returns/$rmaId")({
  component: RfReturnDetail,
});

type Step = "line" | "qty" | "location";
type Condition = "new" | "used" | "damaged" | "destroy";

function RfReturnDetail() {
  const { rmaId } = Route.useParams();
  const { tenantId, facilityId } = useScope();
  const nav = useNavigate();
  const qc = useQueryClient();

  const rmaListFn = useServerFn(listRmas);
  const linesFn = useServerFn(listRmaLines);
  const skusFn = useServerFn(listRmaSkus);
  const lookupFn = useServerFn(lookupLocationByCode);
  const receiveFn = useServerFn(bulkReceiveExpected);

  // RMA header — pulled from the list (already filtered by facility/tenant).
  const { data: rmas = [] } = useQuery({
    queryKey: ["rf-rmas", tenantId, facilityId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => rmaListFn({ data: { tenantId: tenantId!, facilityId: facilityId! } }),
  });
  const rma = (rmas as any[]).find((r) => r.id === rmaId);

  const { data: lines = [], refetch: refetchLines } = useQuery({
    queryKey: ["rf-rma-lines", rmaId],
    queryFn: () => linesFn({ data: { rmaId } }),
  });

  const { data: skus = [] } = useQuery({
    queryKey: ["rf-rma-skus", rma?.customer_id],
    enabled: !!rma?.customer_id,
    queryFn: () => skusFn({ data: { customerId: rma!.customer_id } }),
  });
  const skuMap = useMemo(() => {
    const m = new Map<string, any>();
    for (const s of skus as any[]) m.set(s.id, s);
    return m;
  }, [skus]);

  const [step, setStep] = useState<Step>("line");
  const [lineId, setLineId] = useState<string | null>(null);
  const [qty, setQty] = useState("");
  const [condition, setCondition] = useState<Condition>("used");
  const [locationCode, setLocationCode] = useState("");
  const [locationId, setLocationId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const open = (lines as any[]).filter(
    (l) => (l.qty_received ?? 0) < (l.qty_expected ?? 0),
  );
  const activeLine = (lines as any[]).find((l) => l.id === lineId);
  const activeSku = activeLine ? skuMap.get(activeLine.sku_id) : null;

  // Drop-into-action: when a worker taps a Return task from My Work, land
  // them on the next physical step (quantity entry), not on a single-item
  // line picker. The picker still renders when 2+ lines remain because
  // that's a real operator choice.
  useEffect(() => {
    if (step !== "line" || lineId) return;
    if (open.length === 1) {
      const only = open[0];
      const remaining = (only.qty_expected ?? 0) - (only.qty_received ?? 0);
      setLineId(only.id);
      setQty(String(remaining));
      setStep("qty");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, lineId, open.length]);

  function reset() {
    setStep("line");
    setLineId(null);
    setQty("");
    setCondition("used");
    setLocationCode("");
    setLocationId(null);
  }

  async function resolveLocation(code: string) {
    if (!tenantId || !facilityId) return;
    try {
      const loc: any = await lookupFn({
        data: { tenantId, facilityId, code: code.trim() },
      });
      if (!loc || loc.active === false) {
        toast.error(`Location "${code}" not found`);
        return;
      }
      setLocationCode(loc.code);
      setLocationId(loc.id);
    } catch (e: any) {
      toast.error(e?.message ?? "Lookup failed");
    }
  }

  async function confirm() {
    if (!lineId || !locationId) return;
    const n = parseInt(qty, 10);
    if (!Number.isFinite(n) || n <= 0) return toast.error("Quantity must be ≥ 1");
    const remaining = (activeLine?.qty_expected ?? 0) - (activeLine?.qty_received ?? 0);
    if (n > remaining) return toast.error(`Only ${remaining} left to receive on this line`);
    setBusy(true);
    try {
      const res: any = await receiveFn({
        data: {
          rmaId,
          locationId,
          lines: [{ rmaLineId: lineId, qty: n, condition }],
          deferClassification: condition === "used",
        },
      });
      const failed = (res?.results ?? []).find((r: any) => !r.ok);
      if (failed) throw new Error(failed.error ?? "Receive failed");
      toast.success(`Received ${n}`);
      await Promise.all([
        refetchLines(),
        qc.invalidateQueries({ queryKey: ["rf-rmas", tenantId, facilityId] }),
      ]);
      reset();
    } catch (e: any) {
      toast.error(e?.message ?? "Receive failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <RfShell
      title={`Return ${rma?.rma_number ?? ""}`}
      back="/app/rf/returns"
    >
      {step === "line" && (
        <>
          <div className="text-xs text-muted-foreground px-1">
            {open.length === 0 ? "All lines complete" : `${open.length} line${open.length === 1 ? "" : "s"} remaining`}
          </div>
          {open.length === 0 ? (
            <>
              <RfEmpty>Everything expected on this return has been received.</RfEmpty>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => nav({ to: "/app/rf/returns" })}
              >
                Back to returns
              </Button>
            </>
          ) : (
            <div className="space-y-2">
              {open.map((l) => {
                const sku = skuMap.get(l.sku_id);
                const remaining = (l.qty_expected ?? 0) - (l.qty_received ?? 0);
                return (
                  <button
                    key={l.id}
                    type="button"
                    onClick={() => {
                      setLineId(l.id);
                      setQty(String(remaining));
                      setStep("qty");
                    }}
                    className="w-full text-left rounded-md border bg-card px-3 py-3 active:bg-muted/70"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-mono text-sm truncate">
                          {sku?.sku_code ?? l.sku_id.slice(0, 8)}
                        </div>
                        {sku?.name ? (
                          <div className="text-[11px] text-muted-foreground truncate">
                            {sku.name}
                          </div>
                        ) : null}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-mono text-sm">{remaining}</div>
                        <div className="text-[11px] text-muted-foreground">to receive</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}

      {step === "qty" && (
        <>
          <div className="rounded-md border bg-muted/30 p-3 space-y-1">
            <div className="font-mono text-sm">{activeSku?.sku_code}</div>
            {activeSku?.name ? (
              <div className="text-xs text-muted-foreground">{activeSku.name}</div>
            ) : null}
          </div>
          <div>
            <div className="text-xs font-medium mb-1">How many came back?</div>
            <Input
              inputMode="numeric"
              pattern="[0-9]*"
              autoFocus
              value={qty}
              onChange={(e) => setQty(e.target.value.replace(/\D/g, ""))}
              className="h-14 text-2xl text-center font-mono"
              placeholder="0"
            />
          </div>
          <div>
            <div className="text-xs font-medium mb-1">What condition?</div>
            <Select value={condition} onValueChange={(v) => setCondition(v as Condition)}>
              <SelectTrigger className="h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="new">New / unopened</SelectItem>
                <SelectItem value="used">Used / opened</SelectItem>
                <SelectItem value="damaged">Damaged</SelectItem>
                <SelectItem value="destroy">Destroy on receipt</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-[11px] text-muted-foreground mt-1">
              Inspection and disposition happen later. Just record what you see.
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={reset} disabled={busy}>
              Cancel
            </Button>
            <Button onClick={() => setStep("location")} disabled={!qty}>
              Next
            </Button>
          </div>
        </>
      )}

      {step === "location" && (
        <>
          <div className="rounded-md border bg-muted/30 p-3 space-y-1">
            <div className="font-mono text-sm">{activeSku?.sku_code}</div>
            <div className="text-xs text-muted-foreground">{qty} · {condition}</div>
          </div>
          <div>
            <div className="text-xs font-medium mb-1">Scan destination location</div>
            <ScanInput
              onScan={resolveLocation}
              placeholder="LOC-…"
              size="lg"
              autoFocus
            />
            {locationCode ? (
              <div className="mt-2 text-xs text-emerald-600 dark:text-emerald-400">
                ✓ {locationCode}
              </div>
            ) : null}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Button variant="outline" onClick={() => setStep("qty")} disabled={busy}>
              Back
            </Button>
            <Button onClick={confirm} disabled={busy || !locationId}>
              <CheckCircle2 className="h-4 w-4 mr-1" /> Confirm
            </Button>
          </div>
        </>
      )}
    </RfShell>
  );
}